import { ArrowLeftIcon, ImageIcon, MapTrifoldIcon, PlusIcon } from '@phosphor-icons/react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { useMemo, useState } from 'react';

import { EmptyState } from '@/components/empty-state';
import { ICON_WEIGHT } from '@/components/icon';
import { Button } from '@/components/ui/button';
import { categoryLabel } from '@/features/aircraft';
import { ExportComposer } from '@/features/export-image';
import {
  FlightStats,
  LogbookPanel,
  routeLabel,
  TrackFilesPanel,
  useSummaryForTrack,
} from '@/features/flights';
import { AltitudeProfile } from '@/features/map/AltitudeProfile';
import { FlightMap } from '@/features/map/FlightMap';
import { detectGroundEvents } from '@/features/logbook';
import { DEFAULT_SETTINGS, useSettings } from '@/features/settings';
import { tracks } from '@/lib/db';
import { formatDecimalHours, formatLocalDate, formatLocalTime, localZoneLabel } from '@/lib/format';

/**
 * One flight, looked at properly — `PLAN.md` §6 Phase 2's core view, with
 * everything the rest of the app knows about that flight beside it.
 *
 * The map and the hover-synced profile are the page; the rail alongside them is
 * the join the data model always promised: what the track derives (times,
 * distance, ceiling, speed) and what the logbook says, with the link between
 * the two visible and breakable. Reading the numbers should never mean leaving
 * the map to go and find them.
 */
function TrackDetailPage() {
  const { trackId } = Route.useParams();
  const summary = useSummaryForTrack(trackId);
  const points = useLiveQuery(() => tracks.getPoints(trackId), [trackId], null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  /*
   * Read from the fixes on every visit rather than stored with the flight, so
   * the markers always show what the current detector makes of the track —
   * which is the point of them when a count looks wrong.
   */
  const groundEvents = useMemo(
    () =>
      points === null || points === undefined ? [] : (detectGroundEvents(points)?.events ?? []),
    [points],
  );
  const [exporting, setExporting] = useState(false);
  const settings = useSettings() ?? DEFAULT_SETTINGS;
  const unitPreferences = {
    altitude: settings.altitudeUnit,
    speed: settings.speedUnit,
    distance: settings.distanceUnit,
  };
  const dateOptions = { format: settings.dateFormat, timeZone: settings.timeZone };

  const backLink = (
    <Link
      to="/flights"
      className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 text-sm"
    >
      <ArrowLeftIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
      All flights
    </Link>
  );

  if (summary === null || points === null) {
    return (
      <div className="mx-auto w-full max-w-6xl space-y-4">
        {backLink}
        <p className="text-muted-foreground text-sm">Opening the local database…</p>
      </div>
    );
  }

  if (summary?.track === undefined || points === undefined) {
    return (
      <div className="mx-auto w-full max-w-xl space-y-4">
        {backLink}
        <EmptyState
          icon={MapTrifoldIcon}
          title="Flight not found"
          description="Not in the local database. May have been removed on this device."
        />
      </div>
    );
  }

  const track = summary.track;
  const linkedAircraft = summary.aircraft;
  const registration = track.registration;
  const timeless = track.warnings.some((warning) => warning.kind === 'no-timestamps');
  const durationMs = Date.parse(track.endTime) - Date.parse(track.startTime);
  const aircraftTypeLine =
    linkedAircraft === undefined
      ? ''
      : [linkedAircraft.make, linkedAircraft.model].filter((part) => part !== undefined).join(' ');
  const aircraftLabel =
    aircraftTypeLine !== ''
      ? aircraftTypeLine
      : (linkedAircraft?.icaoType ??
        (linkedAircraft ? categoryLabel(linkedAircraft.category) : ''));
  const route = routeLabel(summary);

  return (
    <div className="mx-auto w-full max-w-6xl space-y-4">
      {backLink}

      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h1 className="text-lg font-semibold tracking-tight">
            {registration ?? track.callsign ?? 'Unknown aircraft'}
          </h1>
          {route === undefined ? null : <span className="text-foreground text-sm">{route}</span>}
          <p className="text-muted-foreground text-sm">
            {timeless ? (
              'Time unknown'
            ) : (
              <>
                {formatLocalDate(track.startTime, dateOptions)}
                {' · '}
                <span className="tabular">{formatLocalTime(track.startTime, dateOptions)}</span>
                {'–'}
                <span className="tabular">{formatLocalTime(track.endTime, dateOptions)}</span>{' '}
                {localZoneLabel(track.startTime, dateOptions)}
                {' · '}
                <span className="tabular">{formatDecimalHours(durationMs / 3_600_000)}</span> hr
              </>
            )}
          </p>
          {/*
            A track only ever carries a registration string, never the
            aircraft record itself — this is that link, made either way: to
            the matching record if one exists, or to creating it pre-filled
            if it doesn't. Nothing to show for a track identified only by
            callsign; there is no registration to match against. It shares
            this row rather than getting one of its own so opening a flight
            never moves the map — the map sits under a fixed-height header on
            every other page, and the import results panel is
            `position: fixed` against the very corner the map's own
            attribution lives in.
          */}
          {linkedAircraft === undefined ? null : (
            <Link
              to="/aircraft/$aircraftId"
              params={{ aircraftId: linkedAircraft.id }}
              className="text-muted-foreground hover:text-foreground text-sm underline-offset-2 hover:underline"
            >
              {aircraftLabel}
            </Link>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/*
            An unknown registration is an action, not more metadata: it used to
            be a grey link in the title row that read like part of the date.
          */}
          {registration === undefined || linkedAircraft !== undefined ? null : (
            <Button variant="outline" size="sm" asChild>
              <Link to="/aircraft/new" search={{ registration }}>
                <PlusIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
                Add {registration} to aircraft list
              </Link>
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            aria-pressed={exporting}
            onClick={() => {
              setExporting((open) => !open);
            }}
          >
            <ImageIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
            Export image
          </Button>
        </div>
      </div>

      {/*
        One column on a phone, map-and-rail on a wide screen. The map keeps its
        own height either way; only the rail moves.
      */}
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="space-y-4">
          <FlightMap
            track={track}
            points={points}
            activeIndex={activeIndex}
            onActiveIndex={setActiveIndex}
            defaultPaletteId={settings.defaultPaletteId}
            unitPreferences={unitPreferences}
            timeZone={settings.timeZone}
          />
          <AltitudeProfile
            points={points}
            activeIndex={activeIndex}
            onActiveIndex={setActiveIndex}
            unitPreferences={unitPreferences}
            timeZone={settings.timeZone}
            timeless={timeless}
            markers={groundEvents}
          />
        </div>
        <div className="space-y-4">
          <FlightStats summary={summary} units={unitPreferences} dateOptions={dateOptions} />
          <LogbookPanel summary={summary} dateOptions={dateOptions} />
          <TrackFilesPanel track={track} points={points} />
        </div>
      </div>

      {exporting ? (
        <ExportComposer
          track={track}
          points={points}
          onClose={() => {
            setExporting(false);
          }}
          defaultOptions={{
            paletteId: settings.defaultPaletteId,
            units: unitPreferences,
            dateFormat: settings.dateFormat,
            timeZone: settings.timeZone,
          }}
        />
      ) : null}
    </div>
  );
}

export const Route = createFileRoute('/flights/$trackId')({
  component: TrackDetailPage,
});
